import { useCallback, useEffect, useState } from 'react';
import { Loader2, Pill, Scale, Info, UserPlus } from 'lucide-react';

import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import portalApi from '../lib/portalApi';
import { usePortalAuth } from '../PortalAuthContext';
import { usePortalDestination } from '../PortalDestinationContext';
import { PortalEmptyState } from '../ui/PortalEmptyState';

export default function PortalDoseHelperPage() {
  const { family, activeMemberId } = usePortalAuth();
  const { href } = usePortalDestination();
  const children = family.filter((m) => m.dob);
  const [memberId, setMemberId] = useState(activeMemberId || '');
  const [guide, setGuide] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!memberId && children.length) setMemberId(children[0].patient_user_id);
  }, [children, memberId]);

  const load = useCallback(async (id) => {
    if (!id) return;
    setLoading(true);
    try {
      const response = await portalApi.get(`/family/${id}/dose-guide`);
      setGuide(response.data);
    } catch { setGuide(null); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(memberId); }, [memberId, load]);

  if (children.length === 0) {
    return (
      <div className="space-y-5" data-testid="portal-dose-helper-page">
        <h1 className="text-2xl font-bold text-slate-900">Dose helper</h1>
        <PortalEmptyState icon={UserPlus} title="Add a child first"
          description="Add a child with their date of birth and weight on the Family profiles page, and their usual doses will show up here."
          testId="portal-dose-helper-empty"
          action={<Button variant="outline" size="sm" onClick={() => { window.location.href = href('family'); }}
            data-testid="portal-dose-helper-add-child">Go to Family profiles</Button>} />
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="portal-dose-helper-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900" data-testid="portal-dose-helper-title">Dose helper</h1>
        <p className="text-sm text-slate-500 mt-1">
          The usual amount for your child, worked out from their weight when you have recorded it.
          Always follow what your pharmacist or doctor tells you.
        </p>
      </div>

      <Select value={memberId} onValueChange={setMemberId}>
        <SelectTrigger className="w-[13rem]" data-testid="portal-dose-helper-child"><SelectValue /></SelectTrigger>
        <SelectContent>
          {children.map((m) => (
            <SelectItem key={m.patient_user_id} value={m.patient_user_id}
              data-testid={`portal-dose-helper-child-${m.patient_user_id}`}>{m.name}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      {loading ? (
        <div className="py-10 text-center text-slate-400" data-testid="portal-dose-helper-loading"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
      ) : !guide ? null : !guide.eligible ? (
        <PortalEmptyState icon={Info} title="No guidance for this profile" description={guide.reason}
          testId="portal-dose-helper-ineligible" />
      ) : (
        <>
          <div className={`rounded-lg border px-4 py-3 text-sm flex items-start gap-2 ${guide.weight_kg ? 'border-emerald-200 bg-emerald-50 text-emerald-900' : 'border-amber-200 bg-amber-50 text-amber-900'}`}
            data-testid="portal-dose-helper-basis">
            <Scale className="w-4 h-4 mt-0.5 shrink-0" />
            <span>
              {guide.weight_kg
                ? <>Worked out from <strong>{guide.name}</strong>’s weight of {guide.weight_kg} kg at {Math.floor(guide.age_years)} years.</>
                : <>No weight on file for <strong>{guide.name}</strong> — these are age-based estimates. Add their weight on Family profiles for a sharper figure.</>}
              {guide.weight_check?.status === 'unlikely' && <> {guide.weight_check.message}</>}
            </span>
          </div>

          <Card>
            <CardContent className="p-0 divide-y" data-testid="portal-dose-helper-list">
              {guide.medicines.map((m) => (
                <div key={m.molecule} className="p-4" data-testid={`portal-dose-${m.molecule}`}>
                  <div className="flex flex-wrap items-center gap-2">
                    <Pill className="w-4 h-4 text-slate-400" />
                    <span className="font-medium">{m.label}</span>
                    <Badge variant="secondary" className="text-[11px]">{m.basis === 'weight' ? 'by weight' : 'by age'}</Badge>
                  </div>
                  <p className="text-sm text-slate-700 mt-1.5">
                    <strong>{m.suggested_dose}</strong> of {m.strength}, {m.suggested_frequency}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    Usual range {m.min_mg}–{m.max_mg} mg per dose · up to {m.max_doses_label || `${m.max_doses_per_day} doses`} a day
                  </p>
                </div>
              ))}
            </CardContent>
          </Card>
          <p className="text-xs text-slate-500" data-testid="portal-dose-helper-disclaimer">
            Guidance only, based on common liquid strengths. Your pharmacist confirms every dose before dispensing.
          </p>
        </>
      )}
    </div>
  );
}
